import { useState, useEffect } from "react";
import {
  CheckCircle,
  XCircle,
  AlertTriangle,
  HelpCircle,
  ChevronDown,
  ChevronUp,
  AlertCircle,
  Loader2,
} from "lucide-react";
import { fetchWithAuth } from "@/lib/api/client";

// Tipos para el resultado del comparador
interface RequirementStatus {
  requirement: string;
  clause_reference: string;
  status: "CUMPLE" | "PARCIAL" | "NO_CUMPLE" | "REVISAR";
  analysis: string;
  recommendation: string;
}

interface Category {
  name: string;
  requirements: RequirementStatus[];
}

interface Summary {
  total: number;
  cumple: number;
  parcial: number;
  no_cumple: number;
  revisar: number;
}

interface ComparatorResult {
  summary: Summary;
  score: number;
  categories: Category[];
  critical_gaps: string[];
  recommendations: string[];
}

interface ComparatorViewProps {
  licitacionId: string;
  initialResult?: ComparatorResult | null;
}

const statusConfig = {
  CUMPLE: {
    icon: CheckCircle,
    color: "text-green-600",
    bg: "bg-green-100",
    label: "Cumple",
  },
  PARCIAL: {
    icon: AlertTriangle,
    color: "text-yellow-600",
    bg: "bg-yellow-100",
    label: "Parcial",
  },
  NO_CUMPLE: {
    icon: XCircle,
    color: "text-red-600",
    bg: "bg-red-100",
    label: "No Cumple",
  },
  REVISAR: {
    icon: HelpCircle,
    color: "text-gray-600",
    bg: "bg-gray-100",
    label: "Revisar",
  },
};

export default function ComparatorView({ licitacionId, initialResult }: ComparatorViewProps) {
  const [companyProfile, setCompanyProfile] = useState("");
  const [focusAreas, setFocusAreas] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<ComparatorResult | null>(null);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

  useEffect(() => {
    // Solo aceptar resultados con la forma esperada del comparador. Si la
    // ejecución viene de otro agente (p.ej. section_critic), descartar para
    // evitar crashes en .map sobre `categories` undefined.
    if (initialResult && Array.isArray(initialResult.categories)) {
      setResult(initialResult);
      setExpandedCategories(new Set(initialResult.categories.map((c: Category) => c.name)));
    }
  }, [initialResult]);

  const availableFocusAreas = [
    "Requisitos Técnicos",
    "Requisitos Económicos",
    "Requisitos Administrativos",
    "Criterios de Adjudicación",
  ];

  const toggleCategory = (categoryName: string) => {
    setExpandedCategories((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(categoryName)) {
        newSet.delete(categoryName);
      } else {
        newSet.add(categoryName);
      }
      return newSet;
    });
  };

  const handleCompare = async () => {
    // Mejora "Perfil empresa": el backend inyecta el perfil global de
    // Configuración → Perfil de empresa. El campo `companyProfile` de
    // este diálogo es OPCIONAL y se trata como "notas adicionales para
    // esta ejecución".
    setIsLoading(true);
    try {
      const data = await fetchWithAuth(`/api/v1/agents/comparator/execute`, {
        method: "POST",
        body: JSON.stringify({
          licitacion_id: licitacionId,
          input_data: {
            mode: "vs_capacidades",
            company_profile: companyProfile,
            focus_areas: focusAreas.length > 0 ? focusAreas : undefined,
          },
        }),
      });
      setResult(data.result);
      // Expandir todas las categorías por defecto al recibir resultados
      setExpandedCategories(new Set(data.result.categories.map((c: Category) => c.name)));
    } catch (error) {
      console.error("Error:", error);
      alert("Error al comparar requisitos. Por favor, intenta nuevamente.");
    } finally {
      setIsLoading(false);
    }
  };

  const toggleFocusArea = (area: string) => {
    setFocusAreas((prev) =>
      prev.includes(area) ? prev.filter((a) => a !== area) : [...prev, area]
    );
  };

  return (
    <div className="space-y-6">
      {/* Descripción del agente */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex gap-3">
        <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-800">
          <p className="font-semibold mb-1">Comparador de Requisitos</p>
          <p>
            Analiza los documentos del pliego y compara cada requisito (técnicos,
            económicos, administrativos) con el perfil de tu empresa. Identifica qué
            requisitos cumples, cuáles parcialmente y dónde hay gaps críticos que
            necesitas resolver antes de presentar la oferta.
          </p>
        </div>
      </div>

      {/* Formulario */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold mb-4 text-gray-800">
          Configuración del Análisis
        </h3>

        {/* Perfil de la empresa */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Notas adicionales sobre la empresa{" "}
            <span className="text-gray-400 font-normal">opcional</span>
          </label>
          <p className="text-xs text-gray-500 mb-2">
            El{" "}
            <a
              href="/configuracion/perfil-empresa"
              className="text-blue-600 hover:underline"
            >
              perfil global de empresa
            </a>{" "}
            ya se inyecta automáticamente. Usa este campo solo para añadir
            detalles puntuales relevantes a este análisis concreto.
          </p>
          <textarea
            value={companyProfile}
            onChange={(e) => setCompanyProfile(e.target.value)}
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            rows={4}
            placeholder="Ej.: para este pliego en concreto, mencionar el equipo que tenemos en Andalucía Oriental…"
          />
        </div>

        {/* Áreas de enfoque */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Áreas de Enfoque (opcional)
          </label>
          <div className="flex flex-wrap gap-2">
            {availableFocusAreas.map((area) => (
              <button
                key={area}
                onClick={() => toggleFocusArea(area)}
                className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                  focusAreas.includes(area)
                    ? "bg-blue-500 text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                {area}
              </button>
            ))}
          </div>
        </div>

        {/* Botón de acción */}
        <button
          onClick={handleCompare}
          disabled={isLoading}
          className="w-full bg-blue-500 text-white font-medium py-2 px-4 rounded-md hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center"
        >
          {isLoading ? (
            <>
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              Comparando...
            </>
          ) : (
            "Comparar Requisitos"
          )}
        </button>
      </div>

      {/* Resultados */}
      {result && (
        <div className="space-y-6">
          {/* Barra de progreso y resumen */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="mb-4">
              <div className="flex justify-between items-center mb-2">
                <h3 className="text-lg font-semibold text-gray-800">
                  Cumplimiento General
                </h3>
                <span className="text-2xl font-bold text-blue-600">
                  {Math.round(result.score * 100)}%
                </span>
              </div>
              {/* Barra de progreso */}
              <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden">
                <div
                  className="bg-blue-500 h-full transition-all duration-500"
                  style={{ width: `${result.score * 100}%` }}
                />
              </div>
            </div>

            {/* Resumen visual */}
            <div className="grid grid-cols-4 gap-4">
              <div className="text-center">
                <div className="flex items-center justify-center mb-1">
                  <CheckCircle className="w-5 h-5 text-green-600 mr-1" />
                  <span className="text-2xl font-bold text-green-600">
                    {result.summary.cumple}
                  </span>
                </div>
                <p className="text-xs text-gray-600">Cumple</p>
              </div>
              <div className="text-center">
                <div className="flex items-center justify-center mb-1">
                  <AlertTriangle className="w-5 h-5 text-yellow-600 mr-1" />
                  <span className="text-2xl font-bold text-yellow-600">
                    {result.summary.parcial}
                  </span>
                </div>
                <p className="text-xs text-gray-600">Parcial</p>
              </div>
              <div className="text-center">
                <div className="flex items-center justify-center mb-1">
                  <XCircle className="w-5 h-5 text-red-600 mr-1" />
                  <span className="text-2xl font-bold text-red-600">
                    {result.summary.no_cumple}
                  </span>
                </div>
                <p className="text-xs text-gray-600">No Cumple</p>
              </div>
              <div className="text-center">
                <div className="flex items-center justify-center mb-1">
                  <HelpCircle className="w-5 h-5 text-gray-600 mr-1" />
                  <span className="text-2xl font-bold text-gray-600">
                    {result.summary.revisar}
                  </span>
                </div>
                <p className="text-xs text-gray-600">Revisar</p>
              </div>
            </div>
          </div>

          {/* Gaps Críticos */}
          {result.critical_gaps && result.critical_gaps.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-red-800 mb-3 flex items-center">
                <AlertCircle className="w-5 h-5 mr-2" />
                Gaps Críticos
              </h3>
              <ul className="space-y-2">
                {result.critical_gaps.map((gap, index) => (
                  <li key={index} className="flex items-start">
                    <span className="inline-block w-6 h-6 rounded-full bg-red-600 text-white text-xs flex items-center justify-center mr-2 mt-0.5 flex-shrink-0">
                      !
                    </span>
                    <span className="text-sm text-red-800">{gap}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Requisitos por categoría */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-800">
              Análisis Detallado por Categoría
            </h3>
            {result.categories.map((category) => {
              const isExpanded = expandedCategories.has(category.name);
              return (
                <div
                  key={category.name}
                  className="bg-white rounded-lg border border-gray-200 overflow-hidden"
                >
                  {/* Header de categoría */}
                  <button
                    onClick={() => toggleCategory(category.name)}
                    className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center">
                      <h4 className="text-md font-semibold text-gray-800">
                        {category.name}
                      </h4>
                      <span className="ml-3 text-sm text-gray-500">
                        ({category.requirements.length} requisitos)
                      </span>
                    </div>
                    {isExpanded ? (
                      <ChevronUp className="w-5 h-5 text-gray-500" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-500" />
                    )}
                  </button>

                  {/* Contenido de categoría */}
                  {isExpanded && (
                    <div className="border-t border-gray-200">
                      <table className="w-full">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                              Estado
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                              Requisito
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                              Análisis
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                              Recomendación
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {category.requirements.map((req, index) => {
                            const statusInfo = statusConfig[req.status];
                            const StatusIcon = statusInfo.icon;
                            return (
                              <tr key={index} className="hover:bg-gray-50">
                                <td className="px-6 py-4">
                                  <div
                                    className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${statusInfo.bg} ${statusInfo.color}`}
                                  >
                                    <StatusIcon className="w-4 h-4 mr-1" />
                                    {statusInfo.label}
                                  </div>
                                </td>
                                <td className="px-6 py-4">
                                  <div className="text-sm text-gray-900">
                                    {req.requirement}
                                  </div>
                                  {req.clause_reference && (
                                    <div className="text-xs text-gray-500 mt-1">
                                      {req.clause_reference}
                                    </div>
                                  )}
                                </td>
                                <td className="px-6 py-4">
                                  <p className="text-sm text-gray-700">
                                    {req.analysis}
                                  </p>
                                </td>
                                <td className="px-6 py-4">
                                  <p className="text-sm text-gray-700">
                                    {req.recommendation}
                                  </p>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Recomendaciones */}
          {result.recommendations && result.recommendations.length > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-blue-800 mb-3">
                Recomendaciones Prioritarias
              </h3>
              <ul className="space-y-2">
                {result.recommendations.map((rec, index) => (
                  <li key={index} className="flex items-start">
                    <span className="inline-block w-6 h-6 rounded-full bg-blue-600 text-white text-xs flex items-center justify-center mr-2 mt-0.5 flex-shrink-0">
                      {index + 1}
                    </span>
                    <span className="text-sm text-blue-800">{rec}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
